import type { DocMigration } from "../docMigration";

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_355: DocMigration = {
  maxSchemaVersion: "0.0.355",
  apply(json: any) {
    const { annotation = {} } = json;
    const annoArr = Object.values(annotation);
    for (const key in annotation) {
      const a = annotation[key];
      if (a.id !== key) {
        //preflight, no actual replacing occurring
        const { count: totalMatchesInEntireDoc } = replaceAllObj(
          json,
          {
            [a.id]: key,
          },
          true,
        );

        if (totalMatchesInEntireDoc < 2) {
          // should be 1
          // just update the one, no problem
          (a as any).id = key;
          continue;
        }

        const matchingKey = !!annotation[a.id];
        const valueCount = annoArr.filter((anno: any) => anno?.id === a.id).length;
        // potentially 1 key + number of values
        const totalMatchesInAnnotationTable = (matchingKey ? 1 : 0) + valueCount;

        // totalMatchesInAnnotation includes the original id
        if (totalMatchesInAnnotationTable > 1) {
          if (totalMatchesInAnnotationTable === totalMatchesInEntireDoc) {
            // all instances are in annotation, we can safely just update the id to match the key, since there are no references elsewhere
            (a as any).id = key;
            continue;
          }

          // this is the scenario we don't want, where the id found in the mismatch is found elsewhere in the annotation table and outside of it
          // let's just update the id in the annotation table and leave the rest alone
          // ran a prod query on 2024-09-05 and found 0 instances of this scenario
          (a as any).id = key;
          continue;
        }

        // match should just be one in the annotation table, so we can safely update the id everywhere

        // otherwise, we can safely update the id everywhere with find-replace since there's only one instance in annotation
        const { result } = replaceAllObj(
          json,
          {
            [a.id]: key,
          },
          true,
        );
        json = result;
      }
    }

    return json;
  },
};

export default migration_0_0_355;

// local copy of replaceAllObj
function replaceAllObj<T extends object>(
  s: T,
  findReplaceTable: { [key: string]: string },
  preflight = false,
): { result: T; count: number } {
  if (Object.keys(findReplaceTable).length === 0) {
    return { result: s, count: 0 };
  }

  const pattern = new RegExp(Object.keys(findReplaceTable).join("|"), "g");
  const minLen = Object.keys(findReplaceTable).reduce((acc, key) => Math.min(acc, key.length), Infinity);

  let count = 0;
  function findReplaceRecInternal(obj: any): any {
    const t = typeof obj;

    if (t === "number" || t === "boolean" || obj === null) {
      // nothing to do for these types
      return obj;
    } else if (t === "string") {
      // if the string is shorter than the shortest key, we can skip it
      if (obj.length < minLen) {
        return obj;
        // if the string is the same length as the shortest key, we can check if it's in the table in constant time
      } else if (obj.length === minLen) {
        const found = findReplaceTable[obj];
        if (found) {
          count++;
          if (preflight) {
            return obj;
          }
          return found;
        }
        return obj;
      } else {
        // otherwise we have to do a regex replace
        return obj.replace(pattern, (match) => {
          count++;
          if (preflight) {
            return match;
          }
          return findReplaceTable[match];
        });
      }
    } else if (t === "object") {
      if (obj) {
        for (const key in obj) {
          const newKey = findReplaceRecInternal(key);
          obj[newKey] = findReplaceRecInternal(obj[key]);
          if (key !== newKey) {
            delete obj[key];
          }
        }
      } else if (Array.isArray(obj)) {
        for (let i = 0; i < obj.length; i++) {
          obj[i] = findReplaceRecInternal(obj[i]);
        }
      }
    }

    return obj;
  }

  const result = findReplaceRecInternal(s);

  return { result, count };
}
